import React from 'react';
import { Card, CardContent, CardActions, Typography, Button, Box, Stack, IconButton } from '@mui/material';
import { Share, Delete, Visibility, Download } from '@mui/icons-material';
import { AmslerGridData } from '../types/AmslerGridData';
import AmslerGridCardCanvas from './AmslerGridCardCanvas';

interface AmslerGridCardProps {
  grid: AmslerGridData;
  onView: () => void;
  onDelete?: () => void;
  onShare: () => void;
  onDownload: () => void;
}

const AmslerGridCard: React.FC<AmslerGridCardProps> = ({ grid, onView, onDelete, onShare, onDownload }) => {
  const date = new Date(grid.timestamp);
  const formattedDate = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  const formattedTime = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
        borderRadius: 3,
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        backgroundColor: '#2A2A2A',
        border: '1px solid #333333',
        width: '80%',
        mx: 'auto',
        '&:hover': {
          boxShadow: '0 8px 16px rgba(255,255,255,0.15)',
          transform: 'translateY(-8px) scale(1.02)',
          cursor: 'pointer',
          borderColor: '#FFFFFF'
        }
      }}
      onClick={onView}
    >
      {/* Grid Preview - Matte black style with rectangular shape */}
      <Box
        sx={{
          height: 259,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: '#202020',
          position: 'relative',
          overflow: 'hidden',
          padding: 1,
        }}
      >
        {/* Debug info to ensure this is the correct grid */}
        {process.env.NODE_ENV === 'development' && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              background: 'rgba(0,0,0,0.5)',
              color: 'white',
              fontSize: '8px',
              padding: '2px 4px',
              zIndex: 10,
              borderRadius: '0 0 4px 0',
            }}
          >
            ID: {grid.id?.substring(0, 8)}
          </Box>
        )}
        
        {/* Render the single grid canvas - Material card style */}
        <Box
          sx={{
            width: '95%',
            height: '95%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '8px',
            overflow: 'hidden',
            bgcolor: '#ffffff',
            boxShadow: 'inset 0 0 2px rgba(255,255,255,0.2)',
            border: '1px solid #333333',
            aspectRatio: '1/1',
            maxHeight: '95%'
          }}
        >
          <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <AmslerGridCardCanvas 
              dots={grid.dots} 
              curves={grid.curves}
              width={160}
              height={160}
            />
          </Box>
        </Box>
      </Box>

      {/* Card Actions - Combine date/time with actions */}
      <CardActions sx={{ pt: 1, pb: 1, px: 2, backgroundColor: '#2A2A2A', justifyContent: 'space-between' }}>
        <Typography 
          variant="body2" 
          sx={{
            fontWeight: 500,
            color: '#E0E0E0',
            lineHeight: 1.2,
            fontSize: '0.9rem'
          }}
        >
          {formattedDate} • {formattedTime}
        </Typography>
        
        <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', gap: '8px' }}>
          <IconButton
            size="small"
            onClick={onDownload}
            title="Download"
            sx={{ 
              padding: '8px',
              color: '#03DAC6',
              mx: 0.2,
              '&:hover': {
                backgroundColor: 'rgba(3, 218, 198, 0.08)',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            <Download fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={onShare}
            title="Share"
            sx={{ 
              padding: '8px',
              color: '#BB86FC',
              mx: 0.2,
              '&:hover': {
                backgroundColor: 'rgba(187, 134, 252, 0.08)',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            <Share fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={onDelete}
            title="Delete"
            sx={{ 
              padding: '8px',
              color: '#CF6679',
              mx: 0.2,
              '&:hover': {
                backgroundColor: 'rgba(207, 102, 121, 0.08)',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease'
            }}
            disabled={!onDelete}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      </CardActions>
    </Card>
  );
};

export default AmslerGridCard; 